import { defineStore } from 'pinia';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { CONVERSATION_STATUS } from 'shared/constants/messages';

// Fixed board columns, in display order.
export const KANBAN_STATUSES = [
  CONVERSATION_STATUS.OPEN,
  CONVERSATION_STATUS.PENDING,
  CONVERSATION_STATUS.RESOLVED,
  CONVERSATION_STATUS.SNOOZED,
];

export const useKanbanStore = defineStore('kanban', {
  state: () => ({
    columns: {
      [CONVERSATION_STATUS.OPEN]: [],
      [CONVERSATION_STATUS.PENDING]: [],
      [CONVERSATION_STATUS.RESOLVED]: [],
      [CONVERSATION_STATUS.SNOOZED]: [],
    },
    uiFlags: {
      fetchingList: false,
      // conversation id currently being confirmed with the API, so its
      // card can show a disabled/loading state while in flight.
      movingConversationId: null,
    },
  }),

  getters: {
    getColumn: state => status => state.columns[status] || [],
  },

  actions: {
    // Each status is a separate paginated list server-side, so the board
    // needs one request per column rather than a single "all conversations"
    // call (the existing conversations store only ever holds one status's
    // worth of conversations at a time, which isn't enough for a board).
    async fetchAll() {
      this.uiFlags.fetchingList = true;
      try {
        const responses = await Promise.all(
          KANBAN_STATUSES.map(status =>
            ConversationApi.get({ status, assigneeType: 'all' })
          )
        );
        KANBAN_STATUSES.forEach((status, index) => {
          this.columns[status] = responses[index].data.data.payload;
        });
      } finally {
        this.uiFlags.fetchingList = false;
      }
    },

    // Confirms a drag-and-drop move with the API. The card itself has
    // already been moved between `columns[...]` arrays by vuedraggable
    // (it mutates the bound :list directly) — this just persists that on
    // the server. Callers should refetch on failure to resync the board,
    // since reverting the exact splice here would duplicate that logic.
    async confirmStatusChange({ conversationId, toStatus }) {
      this.uiFlags.movingConversationId = conversationId;
      try {
        await ConversationApi.toggleStatus({
          conversationId,
          status: toStatus,
        });
      } finally {
        this.uiFlags.movingConversationId = null;
      }
    },
  },
});
